import { useWrapAsync } from "@paroicms/front-app-log";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { useEffect, useState } from "react";
import {
  type ReferenceDatumInput,
  createReferenceDatum,
} from "../../../../api-requests/reference-data";
import {
  type Pagination,
  type PaginationOptions,
  apiRequestWrapper,
} from "../../../../api/shared/http-client";
import { isMultipleChoiceFieldType } from "../../../../helpers/check-schema-field-type";
import type { SchemaFieldOption } from "../../../../helpers/format-databases";
import type { ReferenceDatum } from "../../../../helpers/format-reference-data";
import { getSeq } from "../../../../helpers/get-seq";
import { useAppStore } from "../../../../store/app.store";
import NoDataMessage from "../NoDataMessage/NoDataMessage";
import OurDataTable, { type ColumnType } from "../OurDataTable/OurDataTable";
import Spinner from "../Spinner/Spinner";
import DeleteAction from "./DeleteAction";
import EditAction from "./EditAction";
import makeReferenceDatumFormDialog, {
  type ReferenceDatumFormDialogParameter,
} from "./ReferenceDatumFormDialog/ReferenceDatumFormDialog";
import makeUploadReferenceDataDialog, {
  type UploadReferenceDataDialogParameter,
} from "./UploadReferenceDataDialog";

import { fetchAPIColumnOptions } from "../../../../api/application/table-appendix";
import { fetchTableData } from "../../../../api/application/table-data";
import type { TableData } from "../../../../api/infrastructure/table-data.formatter";
import useGetTableForm from "../../../hooks/useGetTableForm";
import { dayJsFormat } from "../../../shared/global-tools/dayjs.helpers";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import Icon from "../Icons/Icon";
import "./ReferenceDataDataTable.scss";

export default function ReferenceDataDataTable({
  canMutate,
  footer,
  defaultGeneratedReferenceId,
  defaultTableId,
}: {
  canMutate: boolean;
  footer?: boolean;
  defaultTableId: number;
  defaultGeneratedReferenceId: number;
}) {
  const wrapAsync = useWrapAsync();
  const referenceDataById = useAppStore((state) => state.referenceDataById);
  const setReferenceDataById = useAppStore((state) => state.setReferenceDataById);
  const setReferences = useAppStore((state) => state.setGeneratedReferences);
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const [columns, setColumns] = useState<ColumnType<ReferenceDatum>[]>([]);
  const [, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const addReferenceDatum = async (input: ReferenceDatumInput | FormData) => {
    await createReferenceDatum(defaultGeneratedReferenceId, input);
    setReferences(undefined);
  };
  // const handleReferenceDataSearch = async (): Promise<Pagination> => {
  //   const result = await fetchTableData({
  //     referenceId: defaultGeneratedReferenceId,
  //     tableId: defaultTableId,
  //   });
  //   handleSetReferenceData(result);
  //   return {
  //     perPage: result.length,
  //     total: result.length,
  //   };
  // };
  const handleReferenceDataFetch = async (options: PaginationOptions): Promise<Pagination> => {
    const { page = 1, limit = 20 } = options;

    // Get all data first (this could be optimized with server-side pagination later)
    const result = await fetchTableData({
      referenceId: defaultGeneratedReferenceId,
      tableId: defaultTableId,
    });

    // Store all data
    handleSetReferenceData(result);

    // Calculate pagination for local data
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedResult = result.slice(startIndex, endIndex);

    // Update local pagination state
    setCurrentPage(page - 1); // OurDataTable uses 1-based pages, we use 0-based internally
    setPageSize(limit);
    handleSetReferenceData(paginatedResult);

    return {
      perPage: limit,
      total: result.length,
    };
  };
  const handleSetReferenceData = (tableData: TableData[]) => {
    const oldReferenceDataById = new Map(referenceDataById);
    oldReferenceDataById.set(defaultGeneratedReferenceId, tableData);
    setReferenceDataById(oldReferenceDataById);
  };

  useEffect(
    wrapAsync(async () => {
      const oldReferenceData = referenceDataById.get(defaultGeneratedReferenceId);
      if (oldReferenceData !== undefined) return;
      await apiRequestWrapper(async () => {
        await handleReferenceDataFetch({ page: 1, limit: pageSize });
      });
    }),
    [referenceDataById, defaultGeneratedReferenceId, pageSize],
  );
  useEffect(() => {
    if (!defaultGeneratedReferenceId || !defaultTableId) return;
    fetchTableForm({ referenceId: defaultGeneratedReferenceId, tableId: defaultTableId });
  }, [defaultGeneratedReferenceId, defaultTableId]);
  useEffect(
    wrapAsync(async () => {
      if (tableForm.length === 0) return;
      const dynamicColumns = await createDataTableColumns();
      setColumns([
        {
          field: "localId",
          body: localIdBodyTemplate,
          style: { minWidth: "50px" },
        },
        ...dynamicColumns,
        {
          field: "createdAt",
          body: createdAtBodyTemplate,
          style: { minWidth: "120px" },
        },
      ]);
    }),
    [tableForm],
  );

  const headerGroup = (
    <ColumnGroup>
      <Row>
        {canMutate && (
          <Column headerClassName="ReferenceDataDataTable-actionColumns" header="#" colSpan={2} />
        )}
        <Column field="localId" header="#" />
        {tableForm.map((s) => (
          <Column key={s.name} field={s.name} header={s.label} />
        ))}
        <Column field="createdAt" header="Créée le" />
      </Row>
    </ColumnGroup>
  );

  const createDataTableColumns = async () => {
    const cols: ColumnType<ReferenceDatum>[] = [];
    for (const column of tableForm) {
      if (!column.type) continue;
      if (isMultipleChoiceFieldType(column.type)) {
        let options: SchemaFieldOption[] = [];
        if (column.data_source?.type === "key_value") {
          options = column.data_source.values;
        } else if (column.data_source?.type === "api") {
          const apiOptions = await fetchAPIColumnOptions(column.data_source.apiUrl);
          options = apiOptions;
        }
        cols.push({
          field: column.name,
          body: (e) =>
            schemaFieldWithOptionsBodyTemplate({
              referenceDatum: e,
              fieldName: column.name,
              options,
            }),
          style: { minWidth: "120px" },
        });
        continue;
      }
      // TODO implement other special field types
      // if (column.type === "file") {
      //   return {
      //     field: column.name,
      //     body: (e) =>
      //       schemaFieldFileTypeBodyTemplate({
      //         referenceDatum: e,
      //         fieldName: column.name,
      //       }),
      //     style: { minWidth: "100px" },
      //   };
      // }
      if (column.type === "boolean") {
        cols.push({
          field: column.name,
          body: (e) =>
            schemaFieldBooleanTypeBodyTemplate({
              referenceDatum: e,
              fieldName: column.name,
            }),
          style: { minWidth: "50px" },
        });
        continue;
      }
      // TODO implement other special field types
      // if (column.type === "dateTime") {
      //   return {
      //     field: column.name,
      //     body: (e) =>
      //       schemaFieldDatetimeTypeBodyTemplate({
      //         referenceDatum: e,
      //         fieldName: column.name,
      //       }),
      //     style: { minWidth: "100px" },
      //   };
      // }
      cols.push({
        field: column.name,
        style: { minWidth: "120px" },
      });
    }
    return cols;
  };

  const schemaFieldWithOptionsBodyTemplate = ({
    referenceDatum,
    fieldName,
    options,
  }: {
    fieldName: string;
    referenceDatum: ReferenceDatum;
    options: SchemaFieldOption[];
  }) => {
    const value = referenceDatum[fieldName];
    const label = options.find((o) => o.key === value);
    if (label) return <>{label.value}</>;
    return <>{value}</>;
  };
  // const schemaFieldFileTypeBodyTemplate = ({
  //   referenceDatum,
  //   fieldName,
  // }: {
  //   fieldName: string;
  //   referenceDatum: ReferenceDatum;
  // }) => {
  //   const val = referenceDatum[fieldName];
  //   if (!val) {
  //     return <>Aucun fichier</>;
  //   }
  //   if (typeof val !== "string") {
  //     throw new Error("Invalid file type field value");
  //   }
  //   const splittedVal = val.split("/");
  //   return (
  //     <a href={`${BACKEND_BASE_URL}/${val}`} target="_blank" rel="noreferrer">
  //       {splittedVal[splittedVal.length - 1]}
  //     </a>
  //   );
  // };
  const schemaFieldBooleanTypeBodyTemplate = ({
    referenceDatum,
    fieldName,
  }: {
    fieldName: string;
    referenceDatum: ReferenceDatum;
  }) => {
    // TODO improve type checks
    const val = referenceDatum[fieldName];
    if (val === undefined) return;
    return val ? <Icon name="check2" color="#52c41a" /> : <Icon name="cross" color="#ff4d4f" />;
  };
  // const schemaFieldDatetimeTypeBodyTemplate = ({
  //   referenceDatum,
  //   fieldName,
  // }: {
  //   fieldName: string;
  //   referenceDatum: ReferenceDatum;
  // }) => {
  //   const val = referenceDatum[fieldName];
  //   if (!val || typeof val === "number") return;
  //   return dayJsFormat(val);
  // };
  const editActionBodyTemplate = (referenceDatum: ReferenceDatum) => (
    <EditAction
      referenceId={defaultGeneratedReferenceId}
      referenceDatum={referenceDatum}
      schema={[]}
    />
  );
  const deleteActionBodyTemplate = (referenceDatum: ReferenceDatum) => (
    <DeleteAction referenceId={defaultGeneratedReferenceId} referenceDatumId={referenceDatum.id} />
  );
  const createdAtBodyTemplate = (referenceDatum: ReferenceDatum) => (
    <>{dayJsFormat(referenceDatum.createdAt)}</>
  );
  const localIdBodyTemplate = (_referenceDatum: ReferenceDatum, options: { rowIndex: number }) => (
    <>{options.rowIndex + 1}</>
  );

  if (canMutate) {
    columns.unshift({
      key: `deleteColumn-${getSeq()}`,
      body: deleteActionBodyTemplate,
      style: { minWidth: "50px" },
    });
  }
  if (canMutate) {
    columns.unshift({
      key: `editColumn-${getSeq()}`,
      body: editActionBodyTemplate,
      style: { minWidth: "50px" },
    });
  }

  const headerBtns = canMutate
    ? {
        addBtn: {
          label: "Ajouter une ligne",
          onClick: () => {
            syncOpenModalDialog<ReferenceDatumFormDialogParameter>(makeReferenceDatumFormDialog, {
              save: addReferenceDatum,
              schema: [],
              referenceId: defaultGeneratedReferenceId,
              header: "Ajouter une donnée",
              successMessage: "Donnée ajoutée",
              errorMessage: "Echec ajout donnée",
            });
          },
        },
        uploadBtn: {
          label: "Importer des données",
          onClick: () =>
            syncOpenModalDialog<UploadReferenceDataDialogParameter>(makeUploadReferenceDataDialog, {
              referenceId: defaultGeneratedReferenceId,
            }),
        },
      }
    : undefined;

  const referenceData = referenceDataById.get(defaultGeneratedReferenceId);

  if (!referenceData || loadingTableForm) return <Spinner />;
  if (tableForm.length === 0) {
    return <NoDataMessage message="Le référentiel ne possède pas de champs" />;
  }
  return (
    <OurDataTable<ReferenceDatum>
      data={referenceData}
      columns={columns}
      headerGroup={headerGroup}
      options={{
        // TODO implement server side search
        // onSearch: handleReferenceDataSearch,
        stripedRows: true,
        tableStyle: { minWidth: "40rem" },
        headerTitle: !canMutate ? "Liste des Actes Normatifs" : undefined,
        footer,
        headerBtns,
        rows: pageSize,
      }}
      loadData={handleReferenceDataFetch}
    />
  );
}
